import React, { useState, useRef, useEffect } from "react";
import { UserPlus, MessageSquare, Eye, MoreHorizontal } from "lucide-react";
import { useCampaign } from "../campaign-context";
import { CampaignItem } from "./index";

declare const Turbo: {
  visit: (url: string, options?: { frame?: string }) => void;
};

interface ProfileCardActionsProps {
  userId?: number;
  entity: CampaignItem;
}

export function ProfileCardActions({ userId, entity }: ProfileCardActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { setSelectedEntity } = useCampaign();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleAddContact = () => {
    if (!userId) return;
    Turbo.visit(`/app/contacts/bulk_create?recipient_ids[]=${userId}`, {
      frame: "modal",
    });
    setIsOpen(false);
  };

  const handleSendMessage = () => {
    if (!userId) return;
    Turbo.visit(`/app/messages/new?recipient_ids[]=${userId}`, {
      frame: "drawer",
    });
    setIsOpen(false);
  };

  const handleViewProfile = () => {
    setSelectedEntity(entity);
    setIsOpen(false);
  };

  const actions = [
    {
      label: "Ajouter comme contact",
      icon: UserPlus,
      onClick: handleAddContact,
      disabled: !userId,
    },
    {
      label: "Envoyer un message",
      icon: MessageSquare,
      onClick: handleSendMessage,
      disabled: !userId,
    },
    {
      label: "Voir le profil",
      icon: Eye,
      onClick: handleViewProfile,
      disabled: false,
    },
  ];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
      >
        <MoreHorizontal className="w-4 h-4" />
        Actions
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 bottom-full mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="py-1">
            {actions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={index}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${
                    action.disabled
                      ? "text-slate-300 cursor-not-allowed"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
